import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, type SupportTicket, type Tenant } from "@clevis/shared-types";
import { Card, Select, cn } from "@clevis/shared-ui";
import { ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";
import { db } from "../../lib/firebase";
import { TicketThread } from "../../components/TicketThread";

type Row = SupportTicket & { id: string };

const priorityStyle: Record<string, string> = {
  urgent: "bg-danger/15 text-danger",
  high: "bg-brandorange/15 text-brandorange-dark",
  normal: "bg-steel/15 text-ink",
  low: "bg-steel/10 text-steel",
};

export function AdminSupport() {
  const [tickets, setTickets] = useState<Row[]>([]);
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [tab, setTab] = useState<"inbox" | "closed">("inbox");
  const [priority, setPriority] = useState("all");
  const [company, setCompany] = useState("all");
  const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
  const view = useListView(tickets, {
    text: (t) => [t.subject, tenantNames[t.tenantId], t.category, t.priority],
    sorts: {
      updated: (a, b) => b.lastMessageAt - a.lastMessageAt,
      priority: (a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) || b.lastMessageAt - a.lastMessageAt,
      company: (a, b) => (tenantNames[a.tenantId] ?? "").localeCompare(tenantNames[b.tenantId] ?? ""),
    },
    defaultSort: "updated",
    filter: (t) =>
      (tab === "inbox" ? t.status !== "closed" : t.status === "closed") &&
      (priority === "all" || t.priority === priority) &&
      (company === "all" || t.tenantId === company),
  });

  useEffect(() => {
    const subs = [
      onSnapshot(query(collection(db, paths.supportTickets()), orderBy("updatedAt", "desc")), (snap) =>
        setTickets(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SupportTicket) }))),
      ),
      onSnapshot(query(collection(db, paths.tenants())), (snap) => {
        const map: Record<string, string> = {};
        for (const d of snap.docs) map[d.id] = (d.data() as Tenant).name;
        setTenantNames(map);
      }),
    ];
    return () => subs.forEach((u) => u());
  }, []);

  const open = openId ? tickets.find((t) => t.id === openId) : null;
  if (open) {
    return (
      <div className="mx-auto max-w-2xl">
        <p className="mb-1 text-xs text-steel">{tenantNames[open.tenantId] ?? open.tenantId}</p>
        <TicketThread ticketId={open.id} ticket={open} onBack={() => setOpenId(null)} />
      </div>
    );
  }

  const inboxCount = tickets.filter((t) => t.status !== "closed").length;
  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Support Tickets</h1>
        <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
          {(["inbox", "closed"] as const).map((t) => (
            <button
              key={t}
              className={cn("rounded-md px-3 py-1 capitalize", tab === t ? "bg-ink text-white" : "text-steel")}
              onClick={() => setTab(t)}
            >
              {t === "inbox" ? `Inbox (${inboxCount})` : "Closed"}
            </button>
          ))}
        </div>
      </div>
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search subject, company, category…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="all">Any priority</option>
            {["urgent", "high", "normal", "low"].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
          <Select className="w-auto py-1.5 text-sm" aria-label="Company" value={company} onChange={(e) => setCompany(e.target.value)}>
            <option value="all">All companies</option>
            {Object.entries(tenantNames)
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
          </Select>
        </div>
        <div className="hidden items-center gap-4 border-b border-steel/10 px-4 py-2 sm:flex">
          <SortHeader label="Latest" k="updated" view={view} />
          <SortHeader label="Priority" k="priority" view={view} />
          <SortHeader label="Company" k="company" view={view} />
        </div>
        {view.page.map((t) => (
          <button
            key={t.id}
            className="flex w-full items-center justify-between gap-3 border-b border-steel/10 px-4 py-2 text-left last:border-0 hover:bg-cream/60"
            onClick={() => setOpenId(t.id)}
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink">
                {t.unreadForSupport ? <span className="mr-1 inline-block h-2 w-2 rounded-full bg-brandorange" /> : null}
                {t.subject}
              </div>
              <div className="truncate text-xs text-steel">
                {tenantNames[t.tenantId] ?? "?"} · {t.category} ·{" "}
                {new Date(t.lastMessageAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
            <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-xs", priorityStyle[t.priority])}>
              {t.priority}
            </span>
          </button>
        ))}
        {view.filtered.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">
            {view.query || priority !== "all" || company !== "all" ? "Nothing matches." : tab === "inbox" ? "Inbox zero." : "No closed tickets."}
          </div>
        ) : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="tickets" />
      </Card>
    </div>
  );
}
